import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Tooltip,
  Collapse,
  IconButton,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
import api from "../../api/axiosInstance"
import AppBarComponent from "../../components/layout/AppbarComponent";
import { calculateComplexity } from "../../utils/complexityCalculator";
import { ToastContainer, toast } from "react-toastify";
import { useConfirm } from "../../utils/useConfirm";

// --------------------------------------
// CONSTANT
// --------------------------------------
const FUNCTION_TYPES = ["EI", "EO", "EQ", "ILF", "EIF"];
const FUNCTION_DESCRIPTIONS = {
  EI: "External Input — รับข้อมูลจากผู้ใช้หรือระบบภายนอก",
  EO: "External Output — ส่งข้อมูลออกไปภายนอก",
  EQ: "External Inquiry — การสอบถามข้อมูลแบบไม่แก้ไข",
  ILF: "Internal Logical File — ไฟล์ข้อมูลภายในระบบ",
  EIF: "External Interface File — ไฟล์ข้อมูลจากภายนอกระบบ",
};
const isDataType = (t) => ["ILF", "EIF"].includes(t);
const isTxnType = (t) => ["EI", "EO", "EQ"].includes(t);

// --------------------------------------
// NORMALIZE FIELDS BY TYPE
// --------------------------------------
const normalizeFunctionFields = (type, fn) => {
  const updated = { ...fn, function_type: type };

  if (isTxnType(type)) updated.ret = ""; // no RET
  if (isDataType(type)) updated.ftr = ""; // no FTR

  return updated;
};

// --------------------------------------
// MAIN COMPONENT
// --------------------------------------
export default function AddFunction() {
  const { id } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const { confirm } = useConfirm();

  const [softwareData, setSoftwareData] = useState({});
  const [usecases, setUsecases] = useState([]);
  const [library, setLibrary] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState({});

  // --------------------------------------
  // LOAD DATA
  // --------------------------------------
  useEffect(() => {
    const fetchInit = async () => {
      try {
        const sw = await api.get(`api/software/getsoftware/${id}`);
        const uc = await api.get(`api/func/getusecasefunctions/${id}`);
        const lib = await api.get(`api/func/getfunctions/${id}`);

        const processedUCs = uc.data.map((u) => ({
          ...u,
          functions: u.functions.map((f) => ({
            ...f,
            from_library: true,
            is_saved: true,
          })),
        }));
        setUsecases(processedUCs);

        setSoftwareData(sw.data);
        // setUsecases(uc.data);
        setLibrary(lib.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchInit();
  }, [id, API_URL]);

  // --------------------------------------
  // Reload Library
  // --------------------------------------
  const reloadLibrary = async () => {
    try {
      const lib = await api.get(`api/func/getfunctions/${id}`);
      setLibrary(lib.data);
    } catch (err) {
      console.error("Reload library failed: ", err);
    }
  };

  // --------------------------------------
  // ADD NEW FUNCTION ROW
  // --------------------------------------
  const addFunctionRow = (ucIndex) => {
    const updated = [...usecases];
    updated[ucIndex].functions.push({
      function_name: "",
      function_type: "",
      det: "",
      ret: "",
      ftr: "",
      function_complexity: "",
      from_library: false,
      is_saved: false,
    });
    setUsecases(updated);
  };

  // --------------------------------------
  // ADD FROM LIBRARY
  // --------------------------------------
  const addFromLibrary = (ucIndex, fnLib) => {
    const updated = [...usecases];

    const already = updated[ucIndex].functions.some(
      (f) => f.function_id === fnLib.function_id
    );
    if (already) return toast.success("ฟังก์ชันนี้ถูกเพิ่มแล้ว");

    updated[ucIndex].functions.push({
      ...fnLib,
      from_library: true,
      is_saved: false,
    });
    setUsecases(updated);
  };

  // --------------------------------------
  // CHANGE FIELD VALUE
  // --------------------------------------
  const handleFunctionChange = (ucIndex, fnIndex, e) => {
    const { name, value } = e.target;

    const updated = [...usecases];
    let fn = updated[ucIndex].functions[fnIndex];

    fn =
      name === "function_type"
        ? normalizeFunctionFields(value, fn)
        : { ...fn, [name]: value };

    fn.function_complexity = calculateComplexity(
      fn.function_type,
      fn.det,
      fn.ftr,
      fn.ret
    );

    updated[ucIndex].functions[fnIndex] = fn;
    setUsecases(updated);
  };

  // --------------------------------------
  // SAVE ONE FUNCTION
  // --------------------------------------
  const saveOneFunction = async (ucIndex, fnIndex) => {
    const fn = usecases[ucIndex].functions[fnIndex];
    const usecase_id = usecases[ucIndex].usecase_id;

    try {
      //Case1_Library function : attach only
      if (fn.from_library && fn.function_id && !fn.is_saved) {
        await api.post(`api/func/attach`, {
          usecase_id,
          function_id: fn.function_id,
        });

        const updated = [...usecases];
        updated[ucIndex].functions[fnIndex].is_saved = true;
        setUsecases(updated);
        await reloadLibrary();
        toast.success("เพิ่ม Function สำเร็จ");
        return;
      }

      //Case2_New function : create + attach
      if (!fn.is_saved) {
        const res = await api.post(`api/func/addOne`, {
          usecase_id,
          software_id: id,
          function: {
            function_name: fn.function_name,
            function_type: fn.function_type,
            det: fn.det,
            ret: fn.ret,
            ftr: fn.ftr,
            function_complexity: fn.function_complexity,
          },
        });
        const updated = [...usecases];
        updated[ucIndex].functions[fnIndex] = {
          ...fn,
          function_id: res.data.function.function_id,
          from_library: true,
          is_saved: true,
        };

        setUsecases(updated);
        await reloadLibrary();

        toast.success("สร้าง Function ใหม่สำเร็จ");
        return;
      }

      toast.info("บันทึกเรียบร้อย");
    } catch (err) {
      console.error(err);
      toast.error("บันทึกไม่สำเร็จ");
    }
  };

  // --------------------------------------
  // REMOVE FUNCTION
  // --------------------------------------
  const removeFunctionRow = async (ucIndex, fnIndex) => {
    const fn = usecases[ucIndex].functions[fnIndex];
    if (fn.is_saved) {
      const ok = await confirm("ฟังก์ชันนี้ถูกบันทึกแล้ว ต้องการลบใช่หรือไม่?");
      if (!ok) return;

      try {
        await api.post(`api/func/removeFunction`, {
          usecase_id: usecases[ucIndex].usecase_id,
          function_id: fn.function_id,
        });

        await reloadLibrary();
      } catch (err) {
        console.error(err);
        toast.error("ลบฟังก์ชันไม่สำเร็จ");
        return;
      }
    }

    const updated = [...usecases];
    updated[ucIndex].functions.splice(fnIndex, 1);

    if (updated[ucIndex].functions.length === 0) {
      updated[ucIndex].functions.push({
        function_name: "",
        function_type: "",
        det: "",
        ret: "",
        ftr: "",
        function_complexity: "",
        from_library: false,
        is_saved: false,
      });
    }
    setUsecases(updated);
  };

  // --------------------------------------
  // Handle Back/Next FUNCTION
  // --------------------------------------
  const handleBack = async () => {
    const ok = await confirm(
      "หากมีการเปลี่ยนแปลงข้อมูลต้องทำการบันทึกก่อน ต้องการดำเนินการต่อหรือไม่?"
    );
    if (!ok) return;

    navigate(`/software/addusecase/${id}`);
  };

  const handleNext = async () => {
    const ok = await confirm(
      "หากมีการเปลี่ยนแปลงข้อมูลต้องทำการบันทึกก่อน ต้องการดำเนินการต่อหรือไม่?"
    );
    if (!ok) return;

    navigate(`/manhours/${id}`);
  };

  // --------------------------------------
  // ADD TOGGLE FUNCTION
  // --------------------------------------
  const toggleLibrary = (ucIndex) => {
    setLibraryOpen((prev) => ({
      ...prev,
      [ucIndex]: !prev[ucIndex],
    }));
  };

  // =================================================================
  // UI
  // =================================================================
  return (
    <>
      <CssBaseline />
      <AppBarComponent />
      <ToastContainer position="top-center" autoClose={2000} />

      <Box sx={{ p: 2, backgroundColor: "#DFF6F0", minHeight: "100vh" }}>
        <Paper sx={{ width: "95%", m: "0 auto", p: 3 }}>
          <Typography variant="h6" mb={2} sx={{ fontWeight: "bold" }}>
            เพิ่ม Function สำหรับโครงการ : 
          </Typography>
          <Typography variant="h6" mb={2} sx={{ fontWeight: "bold" }}>
            {id}{"_"}
            {softwareData.software_name ? softwareData.software_name : ""}
          </Typography>

          {usecases.map((uc, ucIndex) => (
            <Paper key={ucIndex} sx={{ p: 2, mt: 3 }} elevation={2}>
              <Typography
                variant="subtitle1"
                fontStyle="italic"
                fontWeight="bold"
              >
                Use Case: {uc.usecase_id}_{uc.usecase_name}
              </Typography>

              {/* FUNCTION TABLE */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "25%" }}>Function Name</TableCell>
                      <TableCell sx={{ width: "10%" }}>Type</TableCell>
                      <TableCell sx={{ width: "8%" }}>DET</TableCell>
                      <TableCell sx={{ width: "8%" }}>RET</TableCell>
                      <TableCell sx={{ width: "8%" }}>FTR</TableCell>
                      <TableCell sx={{ width: "15%" }}>Complexity</TableCell>

                      <TableCell colSpan={2} sx={{ width: "26%" }}>
                        การจัดการ
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {uc.functions.map((fn, fnIndex) => (
                      <TableRow key={fnIndex}>
                        <TableCell>
                          <TextField
                            fullWidth
                            name="function_name"
                            disabled={fn.from_library}
                            value={fn.function_name}
                            onChange={(e) =>
                              handleFunctionChange(ucIndex, fnIndex, e)
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            fullWidth
                            select
                            name="function_type"
                            disabled={fn.from_library}
                            value={fn.function_type}
                            onChange={(e) =>
                              handleFunctionChange(ucIndex, fnIndex, e)
                            }
                          >
                            {FUNCTION_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>
                                <Tooltip
                                  title={FUNCTION_DESCRIPTIONS[t]}
                                  arrow
                                  placement="right"
                                >
                                  <Box component="span">{t}</Box>
                                </Tooltip>
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>

                        <TableCell>
                          <TextField
                            fullWidth
                            type="number"
                            name="det"
                            disabled={fn.from_library}
                            value={fn.det}
                            onChange={(e) =>
                              handleFunctionChange(ucIndex, fnIndex, e)
                            }
                            required
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            fullWidth
                            type="number"
                            name="ret"
                            disabled={
                              fn.from_library || !isDataType(fn.function_type)
                            }
                            value={fn.ret}
                            onChange={(e) =>
                              handleFunctionChange(ucIndex, fnIndex, e)
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            fullWidth
                            type="number"
                            name="ftr"
                            disabled={
                              fn.from_library || !isTxnType(fn.function_type)
                            }
                            value={fn.ftr}
                            onChange={(e) =>
                              handleFunctionChange(ucIndex, fnIndex, e)
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            fullWidth
                            disabled
                            value={fn.function_complexity}
                          />
                        </TableCell>

                        <TableCell colSpan={2}>
                          <Box display="flex" gap={1}>
                            <Button
                              variant="contained"
                              color="success"
                              onClick={() => saveOneFunction(ucIndex, fnIndex)}
                              sx={{ fontWeight: "bold" }}
                            >
                              บันทึก
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() =>
                                removeFunctionRow(ucIndex, fnIndex)
                              }
                              sx={{ fontWeight: "bold" }}
                            >
                              ลบ
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
                variant="contained"
                color="info"
                sx={{ mt: 2 }}
                onClick={() => addFunctionRow(ucIndex)}
              >
                + เพิ่ม Function ใหม่
              </Button>

              {/* LIBRARY */}
              {/* LIBRARY HEADER WITH TOGGLE */}
              <Box
                display="flex"
                alignItems="center"
                mt={3}
                sx={{ cursor: "pointer" }}
                onClick={() => toggleLibrary(ucIndex)}
              >
                <Typography variant="subtitle1" fontWeight="bold" flexGrow={1}>
                  Function Library (1 Function ใช้ได้หลาย Usecase)
                </Typography>

                <IconButton size="small">
                  {libraryOpen[ucIndex] ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              {/* COLLAPSIBLE SECTION */}
              <Collapse in={libraryOpen[ucIndex]} timeout="300">
                <TableContainer sx={{ mt: 1 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "25%" }}>
                          Function Name
                        </TableCell>
                        <TableCell sx={{ width: "10%" }}>Type</TableCell>
                        <TableCell sx={{ width: "10%" }}>เพิ่ม</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {library.map((fn) => (
                        <TableRow key={fn.function_id}>
                          <TableCell>{fn.function_name}</TableCell>
                          <TableCell>{fn.function_type}</TableCell>

                          <TableCell>
                            <Button
                              variant="contained"
                              color="info"
                              onClick={() => addFromLibrary(ucIndex, fn)}
                            >
                              เพิ่ม function ใน usecase
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          ))}

          {/* FOOTER BUTTONS */}
          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button
              variant="contained"
              color="inherit"
              onClick={handleBack}
              sx={{ fontWeight: "bold" }}
            >
              ย้อนกลับ
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              sx={{ fontWeight: "bold" }}
            >
              ถัดไป
            </Button>
          </Box>
        </Paper>
      </Box>
    </>
  );
}
